import { ChangeEvent, useEffect, useState } from 'react';

import { getAccessTokenCookie } from '@netfront/common-library';
import { Input, SidebarButtons, Spacing, Spinner, Textarea, ToggleSwitch } from '@netfront/ui-library';
import { useRouter } from 'next/router';

import { useApiKeyContext } from '../../../../contexts';
import { useToast } from '../../../../hooks';
import { IApiKey, IProjectCss } from '../../../../interfaces';

interface IPdfStyles {
  addForms: boolean;
  addLink: boolean;
  addMovies: boolean;
  author: string;
  browserWidth: number;
  fontEmbed: boolean;
  imageQuality: number;
  marginX: number;
  marginY: number;
  mediaType: 'Print';
  pageCacheEnabled: boolean;
  projectId: string;
  repaintDelay: number;
  zoom: number;
}

const CssView = () => {
  const { state: apiKeys = [] } = useApiKeyContext();
  const { handleToastError, handleToastSuccess } = useToast();

  const {
    query: { projectId },
  } = useRouter();

  const [selectedApiKey, setSelectedApiKey] = useState<IApiKey | undefined>(apiKeys.find(({ isSelected }) => isSelected));
  const [style, setStyle] = useState<string>('');
  const [apiKeyCssDetails, setApiKeyCssDetails] = useState<IProjectCss>();
  const [cssFileCount, setCssFileCount] = useState<number>(0);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [pdfStyles, setPdfStyles] = useState<IPdfStyles>({
    addForms: false,
    addLink: false,
    addMovies: false,
    author: '',
    browserWidth: 0,
    fontEmbed: false,
    imageQuality: 0,
    marginX: 0,
    marginY: 0,
    mediaType: 'Print',
    pageCacheEnabled: false,
    projectId: String(projectId),
    repaintDelay: 0,
    zoom: 0,
  });

  useEffect(() => {
    if (!apiKeys.length) {
      return;
    }

    setSelectedApiKey(apiKeys.find(({ isSelected }) => isSelected));
  }, [apiKeys]);


  const savePdfStyles = async (suppliedProjectId: string, accessToken: string) => {
    const body = JSON.stringify({
      ...pdfStyles,
      projectId: suppliedProjectId,
    });
    await fetch(
      `${String(process.env.REACT_APP_LEMUR_API_URL)}/Project/Update`,
      {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Product': 'KANZI',
          'Custom_build_guid': suppliedProjectId,
          'Authorization': `bearer ${String(accessToken)}`,
        },
        body,
      }
    );
  };

  const saveProjectCss = async (suppliedProjectId: string, accessToken: string) => {
    const body = JSON.stringify({
      content: style,
      projectId: suppliedProjectId,
      name: `Api key CSS - ${new Date().toISOString()} - ${String(selectedApiKey?.guid)}`,
      sort: apiKeyCssDetails ? apiKeyCssDetails.sort : cssFileCount + 1,
      id: apiKeyCssDetails ? apiKeyCssDetails.id : undefined,
      tags: [`${String(selectedApiKey?.guid)}`]
    });
    const response = await fetch(
      `${String(process.env.REACT_APP_LEMUR_API_URL)}/Css/addOrUpdate`,
      {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Product': 'KANZI',
          'Custom_build_guid': suppliedProjectId,
          'Authorization': `bearer ${String(accessToken)}`,
        },
        body,
      }
    );
    const data = await response.json();
    const { cssDto } = data as { cssDto: IProjectCss };
    if (!apiKeyCssDetails) {
      setApiKeyCssDetails(cssDto);
    }
    setStyle(cssDto.content);
  };

  const handleSave = async (suppliedProjectId: string) => {
    setIsLoading(true);
    const accessToken = getAccessTokenCookie() ?? '';
    try {
      await Promise.all([
        savePdfStyles(suppliedProjectId, accessToken),
        saveProjectCss(suppliedProjectId, accessToken),
      ]);
    } catch (error) {
      handleToastError({
        error: error as Error,
        shouldUseFriendlyErrorMessage: true,
      });
    } finally {
      handleToastSuccess({ message: 'Styles saved successfully' });
      setIsLoading(false);
    }
  };


  const getApiKeyCss = async (suppliedProjectId: string, cssId: number, accessToken: string) => {
    const response = await fetch(
      `${String(process.env.REACT_APP_LEMUR_API_URL)}/Css?id=${cssId}`,
      {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          'Product': 'KANZI',
          'Custom_build_guid': suppliedProjectId,
          'Authorization': `bearer ${String(accessToken)}`,
        },
      }
    );
    const data = await response.json() as IProjectCss;
    setStyle(data.content);
  };

  const getProjectCss = async (suppliedProjectId: string, accessToken: string) => {
    const response = await fetch(
      `${String(process.env.REACT_APP_LEMUR_API_URL)}/Css/list?projectId=${suppliedProjectId}`,
      {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          'Product': 'KANZI',
          'Custom_build_guid': suppliedProjectId,
          'Authorization': `bearer ${String(accessToken)}`,
        },
      }
    );
    const data = await response.json();
    const { css } = data as { css: IProjectCss[] };
    const foundApiKeyCss = css.find((c) => c.tags.includes(String(selectedApiKey?.guid)));
    setCssFileCount(css.length);
    setApiKeyCssDetails(foundApiKeyCss);

    if (foundApiKeyCss) {
      await getApiKeyCss(suppliedProjectId, foundApiKeyCss.id, accessToken);
    }
  };

  const getProjectPdfStyles = async (suppliedProjectId: string, accessToken: string) => {
    const response = await fetch(
      `${String(process.env.REACT_APP_LEMUR_API_URL)}/Project?projectId=${suppliedProjectId}`,
      {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          'Product': 'KANZI',
          'Custom_build_guid': suppliedProjectId,
          'Authorization': `bearer ${String(accessToken)}`,
        },
      }
    );
    const data = await response.json();
    setPdfStyles(data as IPdfStyles);
  };

  const loadInitialData = async (suppliedProjectId: string) => {
    setIsLoading(true);
    const accessToken = getAccessTokenCookie() ?? '';
    try {
      await Promise.all([
        getProjectCss(suppliedProjectId, accessToken),
        getProjectPdfStyles(suppliedProjectId, accessToken),
      ]);
    } catch (error) {
      handleToastError({
        error: error as Error,
        shouldUseFriendlyErrorMessage: true,
      });
    } finally {
      setIsLoading(false);
    }
  };



  useEffect(() => {
    if (!(projectId && selectedApiKey)) {
      return;
    }



    void loadInitialData(String(projectId));

  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [projectId, selectedApiKey])

  if (!selectedApiKey) {
    return null;
  }

  return (
    <>
      <Spinner isLoading={isLoading} />

      <div className="flex flex-col p-4">
        <section className="flex flex-1 flex-col pb-8">
          <Spacing>
            <Textarea
              id="id_pdf_styles"
              labelText="PDF styles"
              name="pdf-styles"
              tooltipText="These are custom styles that will be applied to override styles in the PDF. It should be a valid CSS string."
              value={style}
              isLabelSideBySide
              onChange={(e) => setStyle(e.target.value)}
            />
          </Spacing>
          <Spacing>
            <ToggleSwitch
              additionalClassNames="c-sidebar-toggle"
              id="addForms"
              inlineLabelPosition="start"
              isChecked={pdfStyles.addForms}
              labelText="Allow forms in the PDF"
              isInline
              onChange={(event) => {
                setPdfStyles((prevStyles) => ({
                  ...prevStyles,
                  addForms: event.target.checked,
                }));
              }}
            />
          </Spacing>
          <Spacing>
            <ToggleSwitch
              additionalClassNames="c-sidebar-toggle"
              id="addLink"
              inlineLabelPosition="start"
              isChecked={pdfStyles.addLink}
              labelText="Allow links in the PDF"
              isInline
              onChange={(event) => {
                setPdfStyles((prevStyles) => ({
                  ...prevStyles,
                  addLink: event.target.checked,
                }));
              }}
            />
          </Spacing>
          <Spacing>
            <ToggleSwitch
              additionalClassNames="c-sidebar-toggle"
              id="addMovies"
              inlineLabelPosition="start"
              isChecked={pdfStyles.addMovies}
              labelText="Allow movies in the PDF"
              isInline
              onChange={(event) => {
                setPdfStyles((prevStyles) => ({
                  ...prevStyles,
                  addMovies: event.target.checked,
                }));
              }}
            />
          </Spacing>
          <Spacing>
            <Input
              id="author"
              labelText="Author"
              name="author"
              tooltipText="Author of the PDF (this will be embedded in the PDF metadata)"
              type="text"
              value={pdfStyles.author}
              onChange={(event: ChangeEvent<HTMLInputElement>) => {
                setPdfStyles((prevStyles) => ({
                  ...prevStyles,
                  author: event.target.value,
                }));
              }}
            />
          </Spacing>
          <Spacing>
            <Input
              id="browserWidth"
              labelText="Browser width"
              name="browserWidth"
              tooltipText="Select the width of the browser to be used when rendering the PDF. This will affect how responsive styles are applied in the PDF."
              type="number"
              value={pdfStyles.browserWidth}
              onChange={(event: ChangeEvent<HTMLInputElement>) => {
                setPdfStyles((prevStyles) => ({
                  ...prevStyles,
                  browserWidth: Number(event.target.value),
                }));
              }}
            />
          </Spacing>
          <Spacing>
            <ToggleSwitch
              additionalClassNames="c-sidebar-toggle"
              id="fontEmbed"
              inlineLabelPosition="start"
              isChecked={pdfStyles.fontEmbed}
              labelText="Allow font embedding in the PDF"
              isInline
              onChange={(event) => {
                setPdfStyles((prevStyles) => ({
                  ...prevStyles,
                  fontEmbed: event.target.checked,
                }));
              }}
            />
          </Spacing>
          <Spacing>
            <Input
              id="imageQuality"
              labelText="Image quality"
              name="imageQuality"
              tooltipText="Select the quality of images to be used in the PDF. This will affect the resolution and file size of the PDF.  "
              type="number"
              value={pdfStyles.imageQuality}
              onChange={(event: ChangeEvent<HTMLInputElement>) => {
                setPdfStyles((prevStyles) => ({
                  ...prevStyles,
                  imageQuality: Number(event.target.value),
                }));
              }}
            />
          </Spacing>
          <Spacing>
            <Input
              id="marginX"
              labelText="Margin X"
              name="marginX"
              tooltipText="Select the horizontal margin to be used in the PDF. This will affect the layout and spacing of the content in the PDF."
              type="number"
              value={pdfStyles.marginX}
              onChange={(event: ChangeEvent<HTMLInputElement>) => {
                setPdfStyles((prevStyles) => ({
                  ...prevStyles,
                  marginX: Number(event.target.value),
                }));
              }}
            />
          </Spacing>
          <Spacing>
            <Input
              id="marginY"
              labelText="Margin Y"
              name="marginY"
              tooltipText="Select the vertical margin to be used in the PDF. This will affect the layout and spacing of the content in the PDF."
              type="number"
              value={pdfStyles.marginY}
              onChange={(event: ChangeEvent<HTMLInputElement>) => {
                setPdfStyles((prevStyles) => ({
                  ...prevStyles,
                  marginY: Number(event.target.value),
                }));
              }}
            />
          </Spacing>
          <Spacing>
            <ToggleSwitch
              additionalClassNames="c-sidebar-toggle"
              id="pageCacheEnabled"
              inlineLabelPosition="start"
              isChecked={pdfStyles.pageCacheEnabled}
              labelText="Enable page cache"
              isInline
              onChange={(event) => {
                setPdfStyles((prevStyles) => ({
                  ...prevStyles,
                  pageCacheEnabled: event.target.checked,
                }));
              }}
            />
          </Spacing>
          <Spacing>
            <Input
              id="repaintDelay"
              labelText="Repaint Delay"
              name="repaintDelay"
              tooltipText="Select the delay for repainting the PDF. This will affect the performance and responsiveness of the PDF rendering."
              type="number"
              value={pdfStyles.repaintDelay}
              onChange={(event: ChangeEvent<HTMLInputElement>) => {
                setPdfStyles((prevStyles) => ({
                  ...prevStyles,
                  repaintDelay: Number(event.target.value),
                }));
              }}
            />
          </Spacing>
          <Spacing>
            <Input
              id="zoom"
              labelText="Zoom"
              name="zoom"
              tooltipText="Select the zoom level for the PDF. This will affect the scaling and readability of the content in the PDF."
              type="number"
              value={pdfStyles.zoom}
              onChange={(event: ChangeEvent<HTMLInputElement>) => {
                setPdfStyles((prevStyles) => ({
                  ...prevStyles,
                  zoom: Number(event.target.value),
                }));
              }}
            />
          </Spacing>
        </section>
        <SidebarButtons
          onSave={() => void handleSave(String(projectId))}
          onSaveButtonType="button"
        />
      </div>
    </>
  );
};

export { CssView };
